import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NAV, type NavLeaf, type NavNode } from '@/config/nav';
import { useAuth } from '@/lib/auth/useAuth';
import { roleHas } from '@/lib/rbac/permissions';
import { useLocale } from '@/lib/i18n/useLocale';
import { authApi } from '@/features/auth/api';
import { useApiError } from '@/lib/query/useApiError';
import { cn } from '@/lib/ui/cn';
import { Wordmark } from '@/lib/ui/Wordmark';
import { AccountMenu } from '@/features/auth/AccountMenu';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { UnreadBadge, useUnreadCounts, type UnreadCounts } from '@/features/leads/opened';

/**
 * Two-tier shell: a sidebar on the cream ground + a thin top utility strip.
 * Ink-and-cream by default: the active nav item is marked by weight, a sand fill
 * and a single ember tick — the one accent in the chrome. RTL-native via logical
 * properties; nav renders from config + RBAC.
 *
 * The sidebar becomes a drawer below 768px, not below 1024px. The old `lg:`
 * breakpoint meant a 900px-wide window — a laptop with a browser beside an
 * editor, which is how this console is actually used — got the mobile hamburger
 * and a 248px drawer over its content. `md:` gives that window its sidebar back
 * and reserves the drawer for the widths that need one.
 *
 * The drawer is a real one: a scrim that dismisses, Escape, a focus trap while
 * it is open, focus returned to the toggle when it closes, and the page behind
 * it locked from scrolling. A slide-in panel with none of those is a menu a
 * keyboard cannot leave.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  const visibleNodes = NAV.map((node) => filterNode(node, role)).filter(Boolean) as NavNode[];
  // B-R16-07: one request for every Leads badge, only for a role that sees a Leads list.
  const { data: unread } = useUnreadCounts(roleHas(role, 'leads:read') || roleHas(role, 'students:read'));

  // A route change closes the drawer even when navigation did not come from a
  // nav link — a redirect after a save, say, or the browser's Back button.
  // Skipped on mount: closing something that was never open is a state update
  // for nothing, on every single page load.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const focusables = () =>
      Array.from(
        drawerRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );

    // Deferred a frame, not called here. The drawer transitions `visibility`
    // as well as `transform` so it can slide OUT before disappearing, and a
    // visibility transition is `hidden` at progress 0 — which is exactly when
    // this effect runs. `.focus()` on a visibility:hidden element is a silent
    // no-op, so focus stayed on the toggle and Tab walked into the content
    // behind the scrim. Measured in a real browser at 360px: the drawer opened
    // with `document.activeElement` still outside it. One frame later the
    // transition has begun and the panel is visible.
    let raf = requestAnimationFrame(() => {
      const first = focusables()[0];
      first?.focus();
      // Belt and braces: if the frame still caught it mid-flip, try once more.
      if (first && document.activeElement !== first) {
        raf = requestAnimationFrame(() => first.focus());
      }
    });

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      // Trap: the drawer covers the page, so Tab must not walk into the content
      // behind it — which is where a keyboard user would otherwise be stranded.
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      // Cancel the pending focus frame: closing the drawer before it fires
      // would otherwise pull focus back INTO a panel that is on its way out,
      // after the line below has already returned it to the toggle.
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      // The ref is read AT CLEANUP on purpose: the toggle to return focus to is
      // the one mounted when the drawer closes, not the one captured when the
      // effect ran. Copying it into a variable, as the rule suggests, would
      // restore focus to a node that may no longer be in the document.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      (toggleRef.current ?? previouslyFocused)?.focus();
    };
  }, [mobileOpen]);

  return (
    <div className="flex min-h-screen bg-canvas-sand">
      {/* Scrim. Only below the sidebar breakpoint, and only while the drawer is
          open: it dims the content the drawer covers and gives the pointer the
          dismiss gesture every drawer is expected to have. `aria-hidden` because
          Escape and the close button are the accessible routes out. */}
      {mobileOpen && (
        <div
          aria-hidden
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-ink-navy/40 md:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        ref={drawerRef}
        id="app-sidebar"
        className={cn(
          'fixed inset-y-0 inset-s-0 z-40 flex w-[280px] max-w-[85vw] flex-col border-e border-hairline bg-canvas-paper text-ink-mid',
          'transition-[transform,visibility] duration-almanac ease-almanac',
          'md:sticky md:top-0 md:h-screen md:w-[248px] md:max-w-none md:visible md:translate-x-0 md:rtl:translate-x-0',
          // `invisible` rather than `aria-hidden`: the sidebar is off-canvas at
          // one breakpoint and permanent at another, and only CSS knows which.
          // visibility:hidden takes it out of the tab order and the a11y tree
          // together, so a closed drawer is not a menu a screen reader can walk
          // into behind the scrim — and `md:visible` gives it straight back.
          mobileOpen ? 'translate-x-0' : 'invisible -translate-x-full rtl:translate-x-full',
        )}
      >
        <div className="flex items-center justify-between gap-sm border-b border-hairline px-lg py-md">
          <Link
            to="/"
            // `py-2xs`: the brand link measured 120×22 in the built console,
            // under the 24px target minimum, because its box was exactly one
            // line of text tall.
            className="flex items-center gap-sm rounded-sm py-2xs text-component-title focus-visible:shadow-focus"
          >
            <Wordmark size={22} withText={false} />
            <span className="text-component-title font-medium text-ink-deep">{t('app.title')}</span>
          </Link>
          {/* The drawer's own dismiss. Hidden once the sidebar is permanent,
              where there is nothing to dismiss. */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label={t('ui.close')}
            className="rounded-sm px-2xs py-2xs text-ink-mid transition-colors duration-almanac hover:text-ink-deep focus-visible:shadow-focus md:hidden"
          >
            <span aria-hidden>×</span>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-sm py-md" aria-label={t('app.primaryNav')}>
          {visibleNodes.map((node) => (
            <NavRenderer key={node.navKey} node={node} unread={unread} onNavigate={() => setMobileOpen(false)} />
          ))}
        </nav>
        {/* The console's identity plate: whose install this is, and which release
            is running. The version is here — always on screen, in every route —
            because the first question in any support exchange is "which version
            are you on?", and an editor cannot answer it from a build artefact.
            It is the compiled-in `__APP_VERSION__`, which Vite replaces with the
            repo-root VERSION file at build time, so it cannot go stale.
            `dir="ltr"` keeps `v0.1.0` from being reordered next to Arabic.
            *
            * The number is announced as a sentence and shown as a glyph: `aria-label`
            * on a <p> is not reliably honoured (the paragraph role does not support an
            * accessible name), so the full string is a visually-hidden child and the
            * compact `v0.1.0` is hidden from assistive technology instead. A screen
            * reader hears "Console version 0.1.0"; an eye sees "v0.1.0". */}
        <div className="flex items-baseline justify-between gap-sm border-t border-hairline px-lg py-sm">
          <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
            {t('app.edition')}
          </p>
          {/* The mono/tracking treatment belongs to the VISIBLE token `v0.1.0`,
              not to the wrapper: on the wrapper the screen-reader sentence
              beside it ("Version 0.1.0") inherits 0.06em of display tracking
              too — a whole sentence set at a label's letter-spacing, which is
              the one thing that treatment is wrong for. */}
          <p dir="ltr" title={t('app.version', { version: __APP_VERSION__ })} className="shrink-0">
            <span className="sr-only">{t('app.version', { version: __APP_VERSION__ })}</span>
            <span aria-hidden="true" className="font-mono text-label-sm tracking-[0.06em] text-ink-mute">
              v{__APP_VERSION__}
            </span>
          </p>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          toggleRef={toggleRef}
          mobileOpen={mobileOpen}
          onToggleMobile={() => setMobileOpen((v) => !v)}
        />
        {/* `p-md` below 768px: at 320px the old 24px gutters spent 15% of the
            viewport on empty margin. */}
        <main id="main" className="mx-auto w-full max-w-[1280px] flex-1 p-md md:p-lg lg:p-xl">
          {children}
        </main>
      </div>
    </div>
  );
}

function TopBar({
  onToggleMobile,
  mobileOpen,
  toggleRef,
}: {
  onToggleMobile: () => void;
  mobileOpen: boolean;
  toggleRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const onError = useApiError();
  // The toggle used to change the language and write it nowhere, so the choice
  // died on the next page load (F-26). It now does both halves of what an
  // operator means by it: this device remembers it immediately (localStorage,
  // inside setLocale), and the profile records it so it follows them to another
  // machine — the same field the account page saves.
  const chooseLocale = (next: 'en' | 'ar') => {
    setLocale(next);
    authApi.updateMe({ locale: next }).catch(onError);
  };
  return (
    <header className="sticky top-0 z-20 flex items-center gap-sm border-b-[0.5px] border-hairline bg-canvas-paper px-md py-sm md:gap-md md:px-lg">
      <button
        ref={toggleRef}
        type="button"
        // 44px square: the toggle was a bare glyph with 8px of side padding,
        // which is a ~20px target on the only widths where it is ever shown.
        className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-sm text-ink-deep transition-colors duration-almanac hover:bg-canvas-bone focus-visible:shadow-focus md:hidden"
        onClick={onToggleMobile}
        aria-label={t('app.toggleNav')}
        aria-expanded={mobileOpen}
        aria-controls="app-sidebar"
      >
        <span aria-hidden>{mobileOpen ? '×' : '☰'}</span>
      </button>
      <div className="flex-1">
        <GlobalSearch />
      </div>
      {/* Language toggle — Canvas Sand pill, announces switch (DESIGN §5 Navigation). */}
      <button
        onClick={() => chooseLocale(locale === 'en' ? 'ar' : 'en')}
        aria-live="polite"
        className="rounded-pill border-[0.5px] border-hairline bg-canvas-sand px-[10px] py-[5px] font-mono text-label uppercase tracking-widest text-ink-deep hover:border-accent-blue focus-visible:shadow-focus"
      >
        {locale === 'en' ? 'عربي' : 'EN'}
      </button>
      <AccountMenu />
    </header>
  );
}

function NavRenderer({
  node,
  unread,
  onNavigate,
}: {
  node: NavNode;
  unread: UnreadCounts | undefined;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  // A list the caller may not read has no key in the counts, so no badge.
  const countOf = (leaf: NavLeaf) => (leaf.unread ? (unread?.[leaf.unread] ?? 0) : 0);
  if (node.type === 'leaf') {
    return <NavLink to={node.to} label={t(`nav.${node.navKey}`)} unread={countOf(node)} onNavigate={onNavigate} />;
  }
  return (
    <div className="mb-sm mt-md">
      <p className="px-sm py-xs font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
        {t(`nav.${node.navKey}`)}
      </p>
      {node.children.map((leaf) => (
        <NavLink
          key={leaf.navKey}
          to={leaf.to}
          label={t(`nav.${leaf.navKey}`)}
          unread={countOf(leaf)}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

/** Every leaf's address, for the longest-match rule below. */
const LEAF_PATHS = NAV.flatMap((n) => (n.type === 'leaf' ? [n.to] : n.children.map((c) => c.to)));

const matches = (to: string, pathname: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

/**
 * Whether `to` is the current page's leaf. The most specific leaf wins, so
 * `/numbers/public` lights "Public numbers" and not "Our numbers" as well.
 */
export function isActiveLeaf(to: string, pathname: string): boolean {
  if (!matches(to, pathname)) return false;
  return !LEAF_PATHS.some((other) => other.length > to.length && other.startsWith(to) && matches(other, pathname));
}

function NavLink({
  to,
  label,
  unread = 0,
  onNavigate,
}: {
  to: string;
  label: string;
  /** B-R16-07: items nobody on the team has opened yet. */
  unread?: number;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = isActiveLeaf(to, pathname);
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        'relative flex items-center gap-xs rounded-sm px-sm py-[9px] text-small transition-colors duration-almanac',
        active
          ? // The tick was `bg-sky-blue`: 2.04:1 on this ground, which is under
            // even the 3:1 non-text floor, so the one mark distinguishing the
            // current page was invisible to anyone it was there for. It is the
            // ember interactive ink now (5.60:1), and 3px rather than 2px.
            'bg-canvas-sand font-semibold text-ink-deep before:absolute before:inset-y-[6px] before:inset-s-0 before:w-[3px] before:rounded-pill before:bg-accent-blue'
          : 'text-ink-mid hover:bg-canvas-bone hover:text-ink-deep',
      )}
    >
      {label}
      {unread > 0 && (
        <>
          <span className="sr-only">
            {t('nav.unopenedSeparator')}
            {t('nav.unopened', { count: unread })}
          </span>
          <UnreadBadge count={unread} />
        </>
      )}
    </Link>
  );
}

/** Hide nodes the role cannot read; drop empty groups. */
function filterNode(node: NavNode, role: ReturnType<typeof useAuth>['role']): NavNode | null {
  if (node.type === 'leaf') return roleHas(role, node.permission) ? node : null;
  const children = node.children.filter((c) => roleHas(role, c.permission));
  return children.length ? { ...node, children } : null;
}
